import { linkedDocumentationInDtoToItems } from 'shared/domain/issue/linkedDocumentation';
import { IssueLocationType } from 'shared/domain/issue/issueModel';
import {
  buildIssueNumber,
  normalizeIssueNumberForSearch,
} from 'shared/domain/issue/issueNumber';
import { IssueInDto } from 'shared/dtos/in/issue';
import { IssueEntityToSave } from './entity';

function collectDescriptionBySourceDocumentId(
  issue: IssueInDto
): Record<string, string> {
  return (issue.primaryData.documents ?? []).reduce<Record<string, string>>(
    (acc, document) => {
      const id = String(document._id ?? '').trim();
      const description = String(document.description ?? '').trim();

      if (!id || !description) {
        return acc;
      }

      acc[id] = description;
      return acc;
    },
    {}
  );
}

export function issueDtoToEntityToSave(
  issue: IssueInDto,
  siteCode: string
): IssueEntityToSave {
  const descriptionBySourceDocumentId =
    collectDescriptionBySourceDocumentId(issue);
  const issueNumberSearch = buildIssueNumber({
    createdAt: issue.createdAt,
    processId: issue.process,
    siteCode,
    hashtag: issue._id.slice(-4),
  });

  return {
    issueNumberSearch,
    issueNumberSearchNormalized:
      normalizeIssueNumberForSearch(issueNumberSearch),
    primaryData: {
      assignee: issue.primaryData.assignee,
      contractNumbers: issue.primaryData.contractNumbers ?? [],
      description: issue.primaryData.description,
      detectionDate: issue.primaryData.detectionDate,
      level: issue.primaryData.level,
      positionOnMap: issue.primaryData.positionOnMap,
      site: issue.primaryData.site,
      subcontractors: issue.primaryData.subcontractors ?? [],
      title: issue.primaryData.title,
      executor: issue.primaryData.executor,
      selectedLocationType:
        issue.primaryData.selectedLocationType || IssueLocationType.pin,
      targetAreas: issue.primaryData.targetAreas ?? [],
      finalAreas: issue.primaryData.finalAreas ?? [],
    },
    extendedData: {
      circumstances: issue.extendedData.circumstances,
      issueVerification: issue.extendedData.issueVerification,
      delayReason: issue.extendedData.delayReason,
      rejectionReason: issue.extendedData.rejectionReason,
      completedAmount: issue.extendedData.completedAmount,
      contaminatedSoilScale: issue.extendedData.contaminatedSoilScale,
      costCode: issue.extendedData.costCode,
      daysOfInabilityToWork: issue.extendedData.daysOfInabilityToWork,
      decisionToImposeFine: issue.extendedData.decisionToImposeFine,
      effect: issue.extendedData.effect,
      environmentalAspect: issue.extendedData.environmentalAspect,
      estimatedCost: issue.extendedData.estimatedCost,
      executedByCompanies: issue.extendedData.executedByCompanies,
      expectedFine: issue.extendedData.expectedFine,
      finalCompletionDate: issue.extendedData.finalCompletionDate,
      targetResponseToAuthorDate:
        issue.extendedData.targetResponseToAuthorDate,
      finalResponseToAuthorDate:
        issue.extendedData.finalResponseToAuthorDate,
      finalCost: issue.extendedData.finalCost,
      finalStartDate: issue.extendedData.finalStartDate,
      hazardCategory: issue.extendedData.hazardCategory,
      impact: issue.extendedData.impact,
      imposedFine: issue.extendedData.imposedFine,
      personUnableToWork: issue.extendedData.personUnableToWork,
      proposedCorrectiveAction:
        issue.extendedData.proposedCorrectiveAction,
      rootCauses: issue.extendedData.rootCauses,
      soilLeakageScale: issue.extendedData.soilLeakageScale,
      solutionMethod: issue.extendedData.solutionMethod,
      solutionProposal: issue.extendedData.solutionProposal,
      spilledSubstance: issue.extendedData.spilledSubstance,
      subcontractorRepresentative:
        issue.extendedData.subcontractorRepresentative,
      targetAmount: issue.extendedData.targetAmount,
      targetCompletionDate: issue.extendedData.targetCompletionDate,
      targetStartDate: issue.extendedData.targetStartDate,
      waterLeakageScale: issue.extendedData.waterLeakageScale,
      workTypes: issue.extendedData.workTypes,
      numberOfEmployees: issue.extendedData.numberOfEmployees,
      priority: issue.extendedData.priority,
      projectStage: issue.extendedData.projectStage,
      sickLeaveStartDate: issue.extendedData.sickLeaveStartDate,
      sickLeaveEndDate: issue.extendedData.sickLeaveEndDate,
      supervisor: issue.extendedData.supervisor,
      victim: issue.extendedData.victim,
      victimJobTitle: issue.extendedData.victimJobTitle,
      employeeNumber: issue.extendedData.employeeNumber,
      employmentType: issue.extendedData.employmentType,
      immediateMeasures: issue.extendedData.immediateMeasures,
      victimActivity: issue.extendedData.victimActivity,
      injuryTypes: issue.extendedData.injuryTypes,
      injuredBodyParts: issue.extendedData.injuredBodyParts,
      applicationNumber: issue.extendedData.applicationNumber,
      productType: issue.extendedData.productType,
      producerDistributor: issue.extendedData.producerDistributor,
      typeModel: issue.extendedData.typeModel,
      installationLocation: issue.extendedData.installationLocation,
      requirements: issue.extendedData.requirements,
      installationBasis: issue.extendedData.installationBasis,
      constructionProductDocuments: issue.extendedData.constructionProductDocuments,
      deviceDocuments: issue.extendedData.deviceDocuments,
      additionalDocuments: issue.extendedData.additionalDocuments,
    },
    deleted: issue.deleted ? 1 : 0,
    inspection: issue.inspection,
    protocolItem: issue.protocolItem,
    process: issue.process,
    stageId: issue.stageId,
    workflowId: issue.workflowId,
    userAccesses: issue.userAccesses,
    events: issue.events,
    workflowDecisions: issue.workflowDecisions ?? [],
    incomingEmailMessages: issue.incomingEmailMessages,
    _id: issue._id,
    createdAt: issue.createdAt,
    modifiedAt: issue.modifiedAt,
    createdBy: issue.createdBy,
    modifiedBy: issue.modifiedBy,
    mainImage: issue.mainImage,
    emailAddress: issue.emailAddress,
    linkedDocumentation: linkedDocumentationInDtoToItems(
      issue.linkedDocumentation,
      descriptionBySourceDocumentId
    ),
  };
}
